/**
 * A2：任意阶段「通过截图」补传 / 查看弹窗。
 * 走 POST /customers/:id/wechat-proof —— 只更新 proof，不动阶段/编号/昵称
 *（区别于 AddedWechatModal 的 confirm-wechat-added：那个是 L0→L1 转换、会改阶段并重写编号）。
 * 因此 L1+ 客户「通过」之后也能在这里补传/替换/查看留证截图。
 */
import { useEffect, useState } from 'react';
import { Modal, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../lib/api';
import { Btn } from './ui';
import { ScreenshotUpload } from './ScreenshotUpload';

export function WechatProofModal({
  open, customerId, onClose,
}: {
  open: boolean;
  customerId: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: proof } = useQuery<any>({
    queryKey: ['wechat-proof', customerId],
    queryFn: () => $.get(`/customers/${customerId}/wechat-proof`),
    enabled: open,
  });
  const [form, setForm] = useState({ proof_screenshot_url: '', avatar_screenshot_url: '' });

  // 打开时用已存 proof 回填（便于"替换"语义）
  useEffect(() => {
    if (open) {
      setForm({
        proof_screenshot_url: proof?.proofScreenshotUrl ?? '',
        avatar_screenshot_url: proof?.avatarScreenshotUrl ?? '',
      });
    }
  }, [open, proof?.proofScreenshotUrl, proof?.avatarScreenshotUrl]);

  const submit = useMutation({
    mutationFn: () => $.post(`/customers/${customerId}/wechat-proof`, { ...form }),
    onSuccess: () => {
      message.success('通过截图已保存');
      qc.invalidateQueries({ queryKey: ['wechat-proof', customerId] });
      onClose();
    },
    onError: (e: any) => message.error(e?.message ?? '保存失败'),
  });

  return (
    <Modal
      title="📎 加微信「通过截图」补传 / 查看"
      open={open}
      onCancel={onClose}
      footer={
        <>
          <Btn kind="default" onClick={onClose}>关闭</Btn>
          <Btn kind="primary" onClick={() => submit.mutate()}
               disabled={!form.proof_screenshot_url || submit.isPending}>
            {submit.isPending ? '保存中…' : '保存截图'}
          </Btn>
        </>
      }
      width={520}
    >
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>
        仅更新加微信合规留证截图，<strong>不改变客户阶段 / 内部编号</strong>。
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, marginBottom: 4, color: 'var(--text-2)' }}>通过截图（必传 — 粘贴/拖拽/选文件，已存则显示当前图，传新图替换）</div>
        <ScreenshotUpload value={form.proof_screenshot_url}
                          onChange={(url) => setForm((f) => ({ ...f, proof_screenshot_url: url }))}
                          entityType="customer" entityId={customerId} fieldName="wechat_proof"
                          enablePaste={open} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, marginBottom: 4, color: 'var(--text-2)' }}>头像截图（选填 — 拖拽/选文件）</div>
        <ScreenshotUpload value={form.avatar_screenshot_url}
                          onChange={(url) => setForm((f) => ({ ...f, avatar_screenshot_url: url }))}
                          entityType="customer" entityId={customerId} fieldName="wechat_avatar"
                          maxHeight={120} />
      </div>
    </Modal>
  );
}
